import { createContext, useContext, useEffect, useState, useMemo } from "react";
import {
  socket,
  connectSocket,
  disconnectSocket,
} from "../services/socket.service";
import { usePermissions } from "./permissions_context";
import cookieStorage from "../utilities/cookie_storage";
import NotificationPopup from "../utilities/notification_popup";

const SocketContext = createContext();

export const SocketProvider = ({ children }) => {
  const { permissions } = usePermissions();
  const [isConnected, setIsConnected] = useState(false);
  
  // Custom notification popup state
  const [notification, setNotification] = useState({
    open: false,
    title: "",
    body: "",
  });

  // Fresh userId whenever permissions change (e.g., on login)
  const userId = useMemo(() => cookieStorage.getItem('userId'), [permissions]);

  useEffect(() => {
    if (!userId) {
      setIsConnected(false);
      disconnectSocket();
      return;
    }

    const showNotification = (msg) => {
      setNotification({
        open: true,
        title: msg?.title || "Notification",
        body: msg?.body || "",
      });
    };

    // Set up listeners first
    socket.on("connect", () => {
      console.log("Socket connected, joining room");
      setIsConnected(true);
      socket.emit("join", userId);
    });

    socket.on("notification", (message) => {
      if (message?.isGlobal) {
        showNotification(message);
        return;
      }
      const sentToList = Array.isArray(message?.sentTo)
        ? message.sentTo
        : [message?.sentTo];

      if (sentToList.includes(userId)) {
        showNotification(message);
      }
    });

    socket.on("disconnect", () => {
      console.log("Socket disconnected");
      setIsConnected(false);
    });

    socket.on("connect_error", (error) => {
      console.error("Socket connection error in context:", error);
    });

    // Then initiate connection
    connectSocket();

    return () => {
      disconnectSocket();
      socket.off("connect");
      socket.off("notification");
      socket.off("disconnect");
      socket.off("connect_error");
    };
  }, [userId]);

  const contextValue = useMemo(() => ({ socket, isConnected }), [isConnected]);

  return (
    <SocketContext.Provider value={contextValue}>
      {children}
      <NotificationPopup
        open={notification?.open}
        title={notification.title}
        body={notification.body}
        onClose={() => setNotification((prev) => ({ ...prev, open: false }))}
      />
    </SocketContext.Provider>
  );
};

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error("useSocket must be used within a SocketProvider");
  }
  return context;
};
